import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/LanguageContext";
import { useAppSub } from "@/context/AppSubContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { LogOut, Lock, ShieldCheck, Ban, FileText, AlertTriangle, Star, Camera, Globe, LayoutGrid, ExternalLink, Images, Play, BadgeCheck, UserCog, KeyRound, Bookmark, CreditCard, Users, RefreshCw } from "lucide-react";
import { euro, initials, timeAgo } from "@/lib/format";
import { toast } from "sonner";
import { LegalFooter } from "@/components/LegalFooter";
import { PushSettings } from "@/components/PushSettings";

const Profile = () => {
  const { user, setUser, logout } = useAuth();
  const { t, lang, setLang, languages } = useI18n();
  const { sub: appSub } = useAppSub();
  const navigate = useNavigate();
  const avatarRef = useRef();
  const [followStats, setFollowStats] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [priv, setPriv] = useState(user?.is_private || false);
  const [subEnabled, setSubEnabled] = useState(user?.sub_enabled || false);
  const [subscribers, setSubscribers] = useState([]);
  const [subCount, setSubCount] = useState(0);
  const [threshold, setThreshold] = useState(10000);
  const [mySubs, setMySubs] = useState([]);
  const [counts, setCounts] = useState({ photos: 0, videos: 0, total: 0 });
  const [acctOpen, setAcctOpen] = useState(false);
  const [acctForm, setAcctForm] = useState({ username: "", email: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [savingAcct, setSavingAcct] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const openAccount = () => {
    setAcctForm({ username: user?.username || "", email: user?.email || "" });
    setPwForm({ current_password: "", new_password: "" });
    setAcctOpen(true);
  };

  const saveAccount = async () => {
    setSavingAcct(true);
    try {
      const body = {};
      if (acctForm.username && acctForm.username !== user?.username) body.username = acctForm.username;
      if (acctForm.email && acctForm.email !== user?.email) body.email = acctForm.email;
      if (Object.keys(body).length === 0) { setSavingAcct(false); return; }
      const { data } = await api.patch("/users/me/account", body);
      setUser(data.user);
      toast.success(t("account.infoSaved"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingAcct(false);
    }
  };

  const changePassword = async () => {
    if (!pwForm.current_password || pwForm.new_password.length < 6) {
      return toast.error("Renseignez le mot de passe actuel et un nouveau (min 6).");
    }
    setSavingPw(true);
    try {
      await api.post("/users/me/password", pwForm);
      toast.success(t("account.passwordChanged"));
      setPwForm({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingPw(false);
    }
  };

  const loadCreatorData = useCallback(async () => {
    try {
      const [subs, mine, wall] = await Promise.all([
        api.get("/creators/me/subscribers"),
        api.get("/subscriptions"),
        api.get("/wall/me"),
      ]);
      setSubscribers(subs.data.subscribers);
      setSubCount(subs.data.count);
      setMySubs(mine.data.subscriptions);
      setCounts(wall.data.counts);
      if (user?.username) {
        const c = await api.get(`/creators/${encodeURIComponent(user.username)}`);
        setThreshold(c.data.unlock_threshold);
      }
    } catch (e) {}
  }, [user]);

  const loadBlocked = useCallback(async () => {
    try {
      const { data } = await api.get("/users/me/blocked");
      setBlocked(data.blocked);
    } catch (e) {}
  }, []);

  useEffect(() => { loadBlocked(); loadCreatorData(); setPriv(user?.is_private || false); setSubEnabled(user?.sub_enabled || false); }, [loadBlocked, loadCreatorData, user]);

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Choisissez une image");
    if (file.size > 4 * 1024 * 1024) return toast.error("Image trop volumineuse (max 4 Mo)");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.patch("/users/me/avatar", { image_base64: reader.result });
        setUser(data.user);
        toast.success("Photo de profil mise à jour");
      } catch (err) {
        toast.error(apiError(err));
      }
    };
    reader.readAsDataURL(file);
  };

  const changeLanguage = async (code) => {
    setLang(code);
    try {
      const { data } = await api.patch("/users/me/language", { language: code });
      setUser(data.user);
    } catch (e) {}
  };

  const saveSubscription = async (enabled) => {
    try {
      const { data } = await api.patch("/users/me/subscription", { enabled, price: 0 });
      setUser(data.user);
      toast.success(enabled ? "Abonnement créateur activé" : "Abonnement créateur désactivé");
    } catch (e) {
      toast.error(apiError(e));
      setSubEnabled(!enabled);
    }
  };

  const togglePrivacy = async (v) => {
    setPriv(v);
    try {
      const { data } = await api.patch("/users/me/privacy", { is_private: v });
      setUser(data.user);
      toast.success(v ? "Compte passé en privé" : "Compte passé en public");
    } catch (e) {
      toast.error(apiError(e));
      setPriv(!v);
    }
  };

  const unblock = async (username) => {
    try {
      await api.post(`/users/${encodeURIComponent(username)}/unblock`);
      toast.success("Utilisateur débloqué");
      loadBlocked();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    if (user?.username) api.get(`/users/${encodeURIComponent(user.username)}/profile`).then(({ data }) => setFollowStats(data.counts)).catch(() => {});
  }, [user?.username]);

  const statusLabel = { trial: "Essai gratuit", active: "Actif", expired: "Expiré" };
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR"); } catch (e) { return "—"; } };

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl">{t("profile.title")}</h1>

      {/* Identity + avatar */}
      <Card className="p-5 rounded-2xl flex items-center gap-4">
        <div className="relative">
          <Avatar className="h-16 w-16">
            {user?.avatar && <AvatarImage src={user.avatar} alt={user.username} />}
            <AvatarFallback className="bg-accent text-accent-foreground text-lg font-semibold">{initials(user?.username)}</AvatarFallback>
          </Avatar>
          <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={onAvatar} data-testid="avatar-input" />
          <button
            onClick={() => avatarRef.current?.click()}
            className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-md hover:opacity-90"
            data-testid="avatar-change-button"
            aria-label={t("profile.avatarChange")}
          >
            <Camera className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="min-w-0">
          <p className="font-display text-xl truncate">@{user?.username}</p>
          <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
          {user?.kyc_status === "pending" && (
            <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-[hsl(38_92%_40%)]"><ShieldCheck className="h-3 w-3" /> {t("profile.kycPending")}</span>
          )}
          {user?.kyc_status === "approved" && (
            <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-[hsl(160_84%_38%)]"><BadgeCheck className="h-3 w-3" /> {t("profile.kycApproved")}</span>
          )}
        </div>
      </Card>

      {/* Wallet mini */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3 rounded-2xl text-center">
          <p className="text-[11px] text-muted-foreground">{t("profile.balance")}</p>
          <p className="font-display text-base" data-testid="profile-balance">{euro(user?.wallet_balance)}</p>
        </Card>
        <Card className="p-3 rounded-2xl text-center">
          <p className="text-[11px] text-muted-foreground">{t("profile.earnings")}</p>
          <p className="font-display text-base">{euro(user?.earnings_total)}</p>
        </Card>
        <Card className="p-3 rounded-2xl text-center">
          <p className="text-[11px] text-muted-foreground">{t("profile.sold")}</p>
          <p className="font-display text-base">{user?.photos_sold ?? 0}</p>
        </Card>
      </div>

      {/* Mon abonnement (app plan) */}
      <Card className="p-4 rounded-2xl" data-testid="profile-my-subscription">
        <div className="flex items-center justify-between mb-3">
          <p className="font-medium text-sm flex items-center gap-2"><CreditCard className="h-4 w-4 text-primary" /> Mon abonnement</p>
          {appSub && (
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${appSub.status === "active" ? "bg-[hsla(160,84%,32%,0.15)] text-[hsl(160_84%_38%)]" : appSub.status === "trial" ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`} data-testid="profile-sub-status">
              {statusLabel[appSub.status] || appSub.status}
            </span>
          )}
        </div>
        {appSub ? (
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Formule</span><span className="font-medium">{appSub.emoji} {appSub.plan_label}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Prix</span><span className="font-medium">{appSub.price === 0 ? "Gratuit" : `${appSub.price?.toFixed(2)} €`}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Début</span><span className="font-medium">{fmtDate(appSub.start_date)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Expiration</span><span className="font-medium">{fmtDate(appSub.end_date)}</span></div>
            <div className="flex gap-2 pt-2">
              <Button size="sm" variant="outline" className="rounded-xl flex-1" onClick={() => navigate("/abonnement")} data-testid="profile-change-plan"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Changer d'abonnement</Button>
              {(appSub.status === "expired" || appSub.status === "trial") && (
                <Button size="sm" className="rounded-xl flex-1" onClick={() => navigate("/abonnement")} data-testid="profile-renew">Renouveler</Button>
              )}
            </div>
          </div>
        ) : (
          <Button size="sm" className="rounded-xl w-full" onClick={() => navigate("/abonnement")}>Choisir un abonnement</Button>
        )}
      </Card>

      {/* Abonnés / Abonnements (follow) */}
      <Card className="p-4 rounded-2xl" data-testid="profile-follow-card">
        <div className="grid grid-cols-3 gap-2 text-center mb-3">
          <div><p className="font-display text-lg">{followStats?.posts ?? counts.total}</p><p className="text-[11px] text-muted-foreground">Publications</p></div>
          <button onClick={() => navigate("/abonnes")} data-testid="profile-open-followers"><p className="font-display text-lg">{followStats?.followers ?? 0}</p><p className="text-[11px] text-muted-foreground">Abonnés</p></button>
          <button onClick={() => navigate("/abonnements")} data-testid="profile-open-following"><p className="font-display text-lg">{followStats?.following ?? 0}</p><p className="text-[11px] text-muted-foreground">Abonnements</p></button>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-xl flex-1" onClick={() => navigate("/abonnes")}><Users className="h-3.5 w-3.5 mr-1" /> Mes abonnés</Button>
          <Button size="sm" variant="outline" className="rounded-xl flex-1" onClick={() => navigate("/abonnements")}><Users className="h-3.5 w-3.5 mr-1" /> Mes abonnements</Button>
        </div>
      </Card>

      {/* Publications (wall) */}
      <Card className="p-4 rounded-2xl">
        <div className="flex items-center justify-between mb-3">
          <p className="font-medium text-sm flex items-center gap-2"><LayoutGrid className="h-4 w-4 text-primary" /> {t("profile.publications")}</p>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" className="rounded-xl h-8" onClick={() => navigate("/mur")} data-testid="profile-open-wall">
              <LayoutGrid className="h-3.5 w-3.5 mr-1" /> {t("nav.wall")}
            </Button>
            <Button size="sm" variant="outline" className="rounded-xl h-8" onClick={() => navigate("/favoris")} data-testid="profile-open-favorites">
              <Bookmark className="h-3.5 w-3.5 mr-1" /> Favoris
            </Button>
            <Button size="sm" variant="outline" className="rounded-xl h-8" onClick={() => navigate(`/c/${user?.username}`)} data-testid="profile-open-vitrine">
              <ExternalLink className="h-3.5 w-3.5 mr-1" /> {t("profile.myWall")}
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl bg-secondary p-2.5 text-center">
            <p className="text-[11px] text-muted-foreground flex items-center justify-center gap-1"><Images className="h-3 w-3" /> {t("profile.photosPublished")}</p>
            <p className="font-display text-base" data-testid="profile-photo-count">{counts.photos}</p>
          </div>
          <div className="rounded-xl bg-secondary p-2.5 text-center">
            <p className="text-[11px] text-muted-foreground flex items-center justify-center gap-1"><Play className="h-3 w-3" /> {t("profile.videosPublished")}</p>
            <p className="font-display text-base" data-testid="profile-video-count">{counts.videos}</p>
          </div>
          <div className="rounded-xl bg-secondary p-2.5 text-center">
            <p className="text-[11px] text-muted-foreground">{t("wall.posts")}</p>
            <p className="font-display text-base">{counts.total}</p>
          </div>
        </div>
      </Card>

      {/* Language */}
      <Card className="p-4 rounded-2xl">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-secondary grid place-items-center"><Globe className="h-4 w-4 text-primary" /></div>
            <div>
              <p className="font-medium text-sm">{t("profile.language")}</p>
              <p className="text-xs text-muted-foreground">{t("profile.languageDesc")}</p>
            </div>
          </div>
          <Select value={lang} onValueChange={changeLanguage}>
            <SelectTrigger className="h-9 w-[140px] rounded-xl" data-testid="profile-language-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languages.map((l) => (
                <SelectItem key={l.code} value={l.code} data-testid={`profile-lang-${l.code}`}>
                  <span className="mr-1">{l.flag}</span> {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Edit account */}
      <Card className="p-4 rounded-2xl">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-secondary grid place-items-center"><UserCog className="h-4 w-4 text-primary" /></div>
            <div>
              <p className="font-medium text-sm">{t("account.title")}</p>
              <p className="text-xs text-muted-foreground">{t("account.desc")}</p>
            </div>
          </div>
          <Button size="sm" variant="outline" className="rounded-xl" onClick={openAccount} data-testid="account-edit-button">
            {t("account.edit")}
          </Button>
        </div>
      </Card>

      {/* Admin moderation (admins only) */}
      {user?.is_admin && (
        <Card className="p-4 rounded-2xl">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-secondary grid place-items-center"><ShieldCheck className="h-4 w-4 text-primary" /></div>
              <div>
                <p className="font-medium text-sm">Modération KYC</p>
                <p className="text-xs text-muted-foreground">Valider / refuser les pièces d'identité.</p>
              </div>
            </div>
            <Button size="sm" variant="outline" className="rounded-xl" onClick={() => navigate("/admin")} data-testid="profile-admin-link">
              Ouvrir
            </Button>
          </div>
        </Card>
      )}

      {/* Privacy */}
      <Card className="p-4 rounded-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-secondary grid place-items-center"><Lock className="h-4 w-4" /></div>
            <div>
              <p className="font-medium text-sm">{t("profile.private")}</p>
              <p className="text-xs text-muted-foreground">{t("profile.privateDesc")}</p>
            </div>
          </div>
          <Switch checked={priv} onCheckedChange={togglePrivacy} data-testid="privacy-private-account-toggle" />
        </div>
      </Card>

      {/* Notifications push (Web Push) */}
      <PushSettings />

      {/* Creator subscription */}
      <Card className="p-4 rounded-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-secondary grid place-items-center"><Star className="h-4 w-4 text-primary" /></div>
            <div>
              <p className="font-medium text-sm">{t("profile.creatorSub")}</p>
              <p className="text-xs text-muted-foreground">{t("profile.creatorSubDesc")}</p>
            </div>
          </div>
          {subCount >= threshold ? (
            <Switch checked={subEnabled} onCheckedChange={(v) => { setSubEnabled(v); saveSubscription(v); }} data-testid="subscription-enable-toggle" />
          ) : (
            <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> {t("profile.locked")}</span>
          )}
        </div>
        {subCount < threshold && (
          <div className="mt-3 text-xs text-muted-foreground" data-testid="subscription-gate">
            {t("profile.subGate")} <strong className="text-foreground">{threshold.toLocaleString("fr-FR")}</strong> {t("profile.subscribers")} <strong className="text-foreground">{subCount}</strong>.
            <div className="mt-1.5 h-1.5 rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${Math.min(100, (subCount / threshold) * 100)}%` }} />
            </div>
          </div>
        )}
      </Card>

      {/* Subscribers & subscriptions */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-3 rounded-2xl text-center">
          <p className="text-[11px] text-muted-foreground">{t("profile.subscribersCount")}</p>
          <p className="font-display text-base" data-testid="profile-subscriber-count">{subCount}</p>
        </Card>
        <Card className="p-3 rounded-2xl text-center">
          <p className="text-[11px] text-muted-foreground">{t("profile.mySubs")}</p>
          <p className="font-display text-base">{mySubs.length}</p>
        </Card>
      </div>

      {subscribers.length > 0 && (
        <Card className="p-4 rounded-2xl">
          <p className="font-medium text-sm flex items-center gap-2 mb-3"><Star className="h-4 w-4 text-primary" /> {t("profile.yourSubscribers")}</p>
          <div className="space-y-2" data-testid="subscribers-list">
            {subscribers.map((s) => (
              <div key={s.user.id} className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  {s.user.avatar && <AvatarImage src={s.user.avatar} alt={s.user.username} />}
                  <AvatarFallback className="bg-accent text-accent-foreground text-[10px]">{initials(s.user.username)}</AvatarFallback>
                </Avatar>
                <span className="flex-1 text-sm">@{s.user.username}</span>
                <span className="text-[11px] text-muted-foreground">{timeAgo(s.since)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Blocked users */}
      <Card className="p-4 rounded-2xl">
        <p className="font-medium text-sm flex items-center gap-2 mb-3"><Ban className="h-4 w-4" /> {t("profile.blocked")}</p>
        {blocked.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t("profile.noBlocked")}</p>
        ) : (
          <div className="space-y-2">
            {blocked.map((b) => (
              <div key={b.id} className="flex items-center gap-3">
                <Avatar className="h-8 w-8"><AvatarFallback className="bg-accent text-accent-foreground text-[10px]">{initials(b.username)}</AvatarFallback></Avatar>
                <span className="flex-1 text-sm">@{b.username}</span>
                <Button size="sm" variant="outline" className="rounded-xl" onClick={() => unblock(b.username)} data-testid={`unblock-${b.username}`}>{t("profile.unblock")}</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Terms / Safety */}
      <Card className="rounded-2xl px-4">
        <Accordion type="single" collapsible>
          <AccordionItem value="tos" className="border-0">
            <AccordionTrigger className="text-sm"><span className="flex items-center gap-2"><FileText className="h-4 w-4" /> {t("profile.tos")}</span></AccordionTrigger>
            <AccordionContent className="text-xs text-muted-foreground space-y-2">
              <p>Plateforme réservée aux 18 ans et plus. Le contenu illégal, impliquant des mineurs ou non consenti est strictement interdit et entraîne un bannissement immédiat.</p>
              <p>Une commission de 20 % est prélevée sur chaque vente.</p>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="safety" className="border-0">
            <AccordionTrigger className="text-sm"><span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> {t("profile.safety")}</span></AccordionTrigger>
            <AccordionContent className="text-xs text-muted-foreground space-y-2">
              <p>Vous pouvez bloquer et signaler tout utilisateur ou photo depuis le menu « … ». Notre équipe examine chaque signalement.</p>
              <p className="flex items-center gap-1 text-[hsl(38_92%_38%)]"><AlertTriangle className="h-3 w-3" /> Ne partagez jamais de contenu sans le consentement des personnes concernées.</p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>

      <Separator />
      <Button variant="outline" className="w-full rounded-xl text-destructive border-destructive/30 hover:bg-destructive/5" onClick={logout} data-testid="logout-button">
        <LogOut className="h-4 w-4 mr-2" /> {t("profile.logout")}
      </Button>
      <LegalFooter className="pt-2" />

      {/* Edit account dialog */}
      <Dialog open={acctOpen} onOpenChange={setAcctOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><UserCog className="h-5 w-5 text-primary" /> {t("account.title")}</DialogTitle>
            <DialogDescription>{t("account.desc")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-xs font-medium text-muted-foreground">{t("account.info")}</p>
            <div className="space-y-1.5">
              <Label htmlFor="acct-username">{t("auth.username")}</Label>
              <Input id="acct-username" value={acctForm.username} onChange={(e) => setAcctForm({ ...acctForm, username: e.target.value })} className="rounded-xl" data-testid="account-username-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acct-email">{t("auth.email")}</Label>
              <Input id="acct-email" type="email" value={acctForm.email} onChange={(e) => setAcctForm({ ...acctForm, email: e.target.value })} className="rounded-xl" data-testid="account-email-input" />
            </div>
            <Button onClick={saveAccount} disabled={savingAcct} className="w-full rounded-xl" data-testid="account-save-info">
              {savingAcct ? t("common.pleaseWait") : t("account.saveInfo")}
            </Button>

            <Separator />

            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> {t("account.changePassword")}</p>
            <div className="space-y-1.5">
              <Label htmlFor="acct-cur">{t("account.currentPassword")}</Label>
              <Input id="acct-cur" type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className="rounded-xl" data-testid="account-current-password" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acct-new">{t("auth.newPassword")}</Label>
              <Input id="acct-new" type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className="rounded-xl" minLength={6} data-testid="account-new-password" />
            </div>
            <Button onClick={changePassword} disabled={savingPw} variant="outline" className="w-full rounded-xl" data-testid="account-change-password">
              {savingPw ? t("common.pleaseWait") : t("account.changePassword")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
